import React, { useMemo, useState } from 'react';
import { fetchStreakCalendar, repairStreakDays } from './adminLabApi';

function formatDateInput(date) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
}

function formatDisplay(dateStr) {
    try {
        const [y, m, d] = dateStr.split('-').map(Number);
        const dt = new Date(y, m - 1, d);
        return dt.toLocaleDateString(undefined, {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
        });
    } catch {
        return dateStr;
    }
}

const StreakRepairTool = ({ token }) => {
    const today = useMemo(() => new Date(), []);
    const [customerId, setCustomerId] = useState('');
    const [startDate, setStartDate] = useState(() =>
        formatDateInput(new Date(today.getTime() - 13 * 86400000))
    );
    const [endDate, setEndDate] = useState(() => formatDateInput(today));
    const [busy, setBusy] = useState(false);
    const [saveBusy, setSaveBusy] = useState(false);
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');
    const [calendar, setCalendar] = useState(null);
    const [selected, setSelected] = useState({});

    const load = async () => {
        setBusy(true);
        setError('');
        setNotice('');
        setSelected({});

        const { error: reqErr, data, status } = await fetchStreakCalendar({
            exigoCustomerId: customerId,
            startDate,
            endDate,
            token,
        });

        setBusy(false);

        if (reqErr) {
            const msg =
                reqErr.response?.data?.message ||
                reqErr.response?.data ||
                reqErr.message ||
                'Could not load streak calendar.';
            setError(
                status === 403
                    ? 'API rejected the request (admin only).'
                    : String(msg)
            );
            setCalendar(null);
            return;
        }

        setCalendar(data);
    };

    const toggleDay = (day) => {
        if (day.qualifies) return;
        setSelected((prev) => ({
            ...prev,
            [day.date]: !prev[day.date],
        }));
    };

    const selectedDates = Object.keys(selected).filter((k) => selected[k]);

    const save = async () => {
        if (!selectedDates.length) {
            setError('Select at least one missing day to repair.');
            return;
        }

        setSaveBusy(true);
        setError('');
        setNotice('');

        const { error: reqErr, data, status } = await repairStreakDays({
            exigoCustomerId: customerId,
            dates: selectedDates,
            token,
        });

        setSaveBusy(false);

        if (reqErr) {
            const msg =
                reqErr.response?.data?.message ||
                reqErr.response?.data ||
                reqErr.message ||
                'Could not repair streak days.';
            setError(
                status === 403
                    ? 'API rejected the repair (admin only).'
                    : String(msg)
            );
            return;
        }

        setNotice(
            `Added ${data.actionsAdded} action(s) across ${data.repairedDates?.length || 0} day(s). Current streak: ${data.currentStreak}, longest: ${data.longestStreak}.`
        );
        await load();
    };

    return (
        <div className="dv2-adminlab__tool">
            <label className="dv2-adminlab__field">
                <span>Customer # (Exigo)</span>
                <input
                    className="dv2-adminlab__input"
                    inputMode="numeric"
                    placeholder="e.g. 3172"
                    value={customerId}
                    onChange={(e) => setCustomerId(e.target.value)}
                />
            </label>

            <div className="dv2-adminlab__daterow">
                <label className="dv2-adminlab__field">
                    <span>From</span>
                    <input
                        className="dv2-adminlab__input"
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                    />
                </label>
                <label className="dv2-adminlab__field">
                    <span>To</span>
                    <input
                        className="dv2-adminlab__input"
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                    />
                </label>
            </div>

            <button
                type="button"
                className="dv2-adminlab__btn"
                disabled={busy || !customerId.trim()}
                onClick={load}
            >
                {busy ? 'Loading…' : 'Load action days'}
            </button>

            {calendar ? (
                <div className="dv2-adminlab__streakmeta">
                    <span>
                        Oola user #{calendar.oolaUserId} · TZ{' '}
                        {calendar.timeZoneId}
                    </span>
                    <span>
                        Streak {calendar.currentStreak} / longest{' '}
                        {calendar.longestStreak}
                    </span>
                </div>
            ) : null}

            {calendar?.days?.length ? (
                <ul className="dv2-adminlab__daylist">
                    {calendar.days.map((day) => {
                        const qualifies = day.qualifies || day.count >= 3;
                        const checked = qualifies || Boolean(selected[day.date]);
                        return (
                            <li
                                key={day.date}
                                className={`dv2-adminlab__day${
                                    qualifies
                                        ? ' dv2-adminlab__day--ok'
                                        : ' dv2-adminlab__day--missing'
                                }`}
                            >
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={checked}
                                        disabled={qualifies}
                                        onChange={() => toggleDay(day)}
                                    />
                                    <span className="dv2-adminlab__daylabel">
                                        {formatDisplay(day.date)}
                                    </span>
                                    <span className="dv2-adminlab__daycount">
                                        {day.count} action
                                        {day.count === 1 ? '' : 's'}
                                    </span>
                                </label>
                            </li>
                        );
                    })}
                </ul>
            ) : null}

            {calendar ? (
                <button
                    type="button"
                    className="dv2-adminlab__btn dv2-adminlab__btn--gold"
                    disabled={saveBusy || !selectedDates.length}
                    onClick={save}
                >
                    {saveBusy
                        ? 'Saving…'
                        : `Restore selected days (${selectedDates.length})`}
                </button>
            ) : null}

            <p className="dv2-adminlab__hint">
                A streak day needs at least 3 completed actions. Missing days are
                highlighted; select them and save to backfill up to 3 actions per
                day.
            </p>

            {notice ? (
                <p className="dv2-adminlab__notice" role="status">
                    {notice}
                </p>
            ) : null}
            {error ? (
                <p className="dv2-adminlab__error" role="alert">
                    {error}
                </p>
            ) : null}
        </div>
    );
};

export default StreakRepairTool;
